import { IHideSetting, ILayout } from 'types/layoutInterfaces'

/**
 * The item details a layout can hide, by hide setting type. The order is the order of the checkboxes in the
 * layout controls, and each detail has a `print.hideSetting.<detail>` label.
 */
export const hideSettingDetails = {
  employments: ['name', 'date', 'primaryRole', 'roles', 'description'],
  projects: [
    'name',
    'date',
    'primaryRole',
    'projectRoles',
    'customerName',
    'employerName',
    'description',
    'personSkills',
    'industries',
  ],
  degrees: ['name', 'date', 'degree', 'description'],
  courses: ['name', 'date', 'organizer', 'description'],
  certificates: ['name', 'date', 'certifier', 'url', 'description'],
  competences: ['name', 'date', 'issuer', 'description'],
  references: ['name', 'company', 'letter'],
  leisureActivities: ['name', 'date', 'activityType', 'description'],
} as const

export type HideSettingType = keyof typeof hideSettingDetails

/** The print sections whose data key differs from their hide setting type. */
const sectionHideSettingTypes: Record<string, HideSettingType> = {
  personCertificates: 'certificates',
  personCompetences: 'competences',
}

/**
 * Finds the hide setting of a print section in a layout.
 *
 * @param {ILayout} layout - The layout, if any.
 * @param {string} section - The section's hide setting type or its CV data key (e.g. `personCertificates`).
 * @return {IHideSetting | undefined} The section's hide setting, or undefined when the layout has none.
 */
export const findHideSetting = (layout: ILayout | undefined, section: string): IHideSetting | undefined => {
  const type = sectionHideSettingTypes[section] ?? section
  return layout?.HideSettings?.find(setting => setting.type === type)
}

/**
 * Tells whether a hide setting hides an item detail.
 *
 * @param {IHideSetting} hideSetting - The section's hide setting, if any.
 * @param {string} detail - The detail, e.g. `description`.
 * @return {boolean} True when the detail is hidden.
 */
export const isDetailHidden = (hideSetting: IHideSetting | undefined, detail: string): boolean =>
  hideSetting?.data?.[detail] === true

/**
 * Tells whether a hide setting hides any of the section's item details.
 *
 * @param {IHideSetting} hideSetting - The section's hide setting, if any.
 * @param {HideSettingType} type - The section's hide setting type.
 * @return {boolean} True when at least one detail is hidden.
 */
export const hasHiddenDetails = (hideSetting: IHideSetting | undefined, type: HideSettingType): boolean =>
  hideSettingDetails[type].some(detail => isDetailHidden(hideSetting, detail))
